/**
 * Query Params used frequently to represent search state. Check out existing usages before adding a new one :)
 *
 * To add a new one, make sure to update the query state and query state parsed interfaces as well!
 * That would be in @/utils/hooks/use-query-state
 */
export enum DynamicQueryParam {
  /** General text-matching search, primary search on page */
  QUERY_STRING = "q",
  TABLE_ROW_HEIGHT = "rowHeight",
  CLUSTER_ID = "clusterId",
  TEST_COMPONENTS_PANE = "pane",
  TEST_COMPONENTS_EXAMPLE = "example",
  TEST_COMPONENTS_SAMPLE_PLAN = "samplePlan",
  TEST_COMPONENTS_DATA_STATE = "data-state",
  /** Content id of the metadata ZIP open in the Metadata ZIP viewer pane. */
  TEST_COMPONENTS_METADATA_ZIP = "zip",
  /** For user-based filters */
  AGENT_ID = "aid",
  /** Audit logs endpoint filter */
  AUDIT_ENDPOINTS = "aeps",
  /** For branch filters */
  BRANCH = "br",
  CONFIG = "config",
  /** For deployment id filters */
  DEPLOYMENT_ID = "did",
  DEPLOYMENT_IDS = "dids",
  /** For query name filters */
  QUERY_NAME = "qn",
  /** For query_name_version filters on meta-query-derived listings */
  QUERY_VERSION = "qv",
  /** For meta_query_id filters on the consolidated Online Queries tab */
  META_QUERY_ID_FILTER = "mqid",
  /** For correlation id filters */
  CORRELATION_ID = "cid",
  /** For success/error status filter chips (serialized pill filter) */
  STATUS_FILTER = "stat",
  MCP_TRANSPORT_FILTER = "mcp-transport",
  MCP_CREDENTIAL_FILTER = "mcp-credential",
  /** For with/without-traces filter chips (serialized pill filter) */
  TRACES_FILTER = "trcs",
  /** For started-within lookback filter chips (serialized pill filter) */
  STARTED_LOOKBACK = "strt",
  /** For resource group filters */
  RESOURCE_GROUP = "rgid",
  /** Model Gateway usage table filter chips. */
  ROUTER_USAGE_API_KEY_FILTER = "ruak",
  ROUTER_USAGE_API_KEY_LABEL_FILTER = "ruakl",
  ROUTER_USAGE_CONNECTION_FILTER = "ruc",
  ROUTER_USAGE_END_USER_FILTER = "rueu",
  ROUTER_USAGE_MODEL_FILTER = "rum",
  ROUTER_USAGE_MODEL_ROUTE_FILTER = "rumr",
  ROUTER_USAGE_POOL_FILTER = "rup",
  /** For trace id filters */
  TRACE_ID = "tid",
  /** Page view selection */
  PAGE_VIEW = "view",
  /** Minimum trace duration filter in milliseconds */
  TRACE_MIN_DURATION_MS = "tdmin",
  /** Maximum trace duration filter in milliseconds */
  TRACE_MAX_DURATION_MS = "tdmax",
  /** Minimum trace span count */
  TRACE_MIN_SPAN_COUNT = "tsmin",
  /** Maximum trace span count */
  TRACE_MAX_SPAN_COUNT = "tsmax",
  /** For environment filters */
  ENVIRONMENT = "e",
  /** For selects that show pre-set date ranges */
  DATE_RANGE_PRESET = "drp",
  /** Start time of search, expects ISO string */
  START_DATE = "ds",
  /** End time of search, expects ISO string */
  END_DATE = "de",
  /** Relative lookback window in milliseconds; when set, the range ends "now" and wins over START_DATE/END_DATE */
  LOOKBACK_MS = "lb",
  ERROR_STATUS = "es",
  /** Selected evaluation run on the evaluations page */
  EVALUATION_RUN_ID = "erun",
  /** Plotted evaluation runs; absent means all, an empty list means none. */
  EVALUATION_RUN_IDS = "eruns",
  EVALUATION_CHART_TAB = "echart",
  EVALUATION_CHART_DISPLAY = "edisplay",
  /** Selected chart fields, namespaced by chart tab; absent means all. */
  EVALUATION_SERIES = "eseries",
  /** Active view (results table, summary, or run trace) of the selected evaluation run */
  EVALUATION_RUN_VIEW = "erview",
  /** Selected post-training on an evaluation's page */
  EVALUATION_POST_TRAINING_ID = "eptr",
  /** Selected evaluation suite on the evaluations page */
  EVALUATION_SUITE_ID = "esuite",
  /** Evaluations on the comparison page; the first is the one compared against */
  EVALUATION_COMPARE = "ecmp",
  /** Minimum latency filter for query runs (milliseconds) */
  LATENCY_MS = "lms",
  FEATURE_TAGS = "ft",
  FEATURE_FQN = "ffqn",
  HAS_TRACE = "ht",
  NAMESPACE = "ns",
  NAMESPACES = "nses",
  /** filtering features that are inputs or outputs to a named query **/
  NAMED_QUERY = "nq",
  NAMED_QUERIES = "nqs",
  /** filtering features that are outputs of a named query; NAMED_QUERIES covers the input-to side */
  OUTPUT_OF_NAMED_QUERIES = "onqs",
  FEATURE_TYPES = "ftypes",
  FEATURE_FILTERS = "ffils",
  /** Filter text for spans/traces */
  FILTER = "filter",
  /** Related to offline queries */
  OFFLINE_QUERY_TYPE_PROTO = "oqs_p",
  OFFLINE_QUERY_STATUS_PROTO = "oqt_p",
  /** Row target for the offline query utilization table; the table pages until it is met. */
  OFFLINE_QUERY_UTILIZATION_LIMIT = "oqu_lim",
  OFFLINE_QUERY_TYPES = "offline_query_types",
  OFFLINE_QUERY_STATUSES = "offline_query_statuses",
  /** Related to script tasks */
  SCRIPT_TASK_STATUS_PROTO = "sts_p",
  /** Related to SQL worksheets */
  WORKSHEET_ID = "WS_ID",
  /** Related to Argo build workflows list */
  ARGO_BUILD_PHASE_PROTO = "abp_p",
  /** Related to SQL query history */
  SQL_QUERY_STATUS = "sqs",
  /** Related to dataframe run history */
  DF_RUN_STATUS = "dfrs",
  /** A feature's owner, as an email */
  OWNER_EMAIL = "oe",
  OWNER_EMAILS = "oes",
  /** Operation Status, as an enum*/
  OPERATION_STATUS = "so",
  // Used in the Query Plan Viewer
  OPERATOR = "operator",
  PLAN_DETAILS_SELECTION = "planDetail",
  /** Query Plan Viewer settings panel, encoded compactly (see planViewSettingsUrl). */
  PLAN_VIEW_SETTINGS = "planview",
  PAGE = "p",
  /** Opaque cursor for the unified named-queries listing pagination. */
  NAMED_QUERIES_PAGE_TOKEN = "pt",
  /** Version slot for the named-query detail page (/queries/named/<name>?ver=<version>). */
  NAMED_QUERY_VERSION = "ver",
  RESOLVER_CONTEXT = "rc",
  RESOLVER_FQN = "rfqn",
  RESOLVER_FQNS = "rfqns",
  SORTED_COLUMNS = "colsort",
  /** If a page has multiple tabs/views that are not stored in fragment #, use view*/
  VIEW = "v",

  /** Note: This Query Param generally is used with useQueryVisibleColumns hook that provides column name shortening */
  VISIBLE_COLUMNS = "cols",
  /** Note: This Query Param generally is used with table hooks that provide column name shortening */
  FROZEN_COLUMNS = "fcols",
  /** grouping view for the kubernetes settings pane */
  KUBERNETES_GROUP_VIEW = "kgv",
  /** should the kubernetes view show pods from the current environment only */
  KUBERNETES_ONLY_CURRENT_ENV_PODS = "kocep",
  /** should the kubernetes view include pods without an environment label */
  KUBERNETES_INCLUDE_NON_ENVIRONMENT_PODS = "kinep",
  /** should the kubernetes view show DaemonSet pods */
  KUBERNETES_SHOW_DAEMONSET_PODS = "ksdp",
  /** filter for kubernetes nodes */
  KUBERNETES_NODE_FILTER = "knf",
  /** filter for kubernetes pods */
  KUBERNETES_POD_FILTER = "kpf",
  /** sort for kubernetes nodes */
  KUBERNETES_NODE_SORT = "kns",
  /** text filter for Kubernetes Gateway API routes */
  KUBERNETES_ROUTE_FILTER = "krf",

  /** show deactivated team members in the user permissions settings pane */
  TEAM_MEMBERS_SHOW_DEACTIVATED = "tmsd",

  /** Data lineage page query params */
  DATA_LINEAGE_DATASOURCE = "dlinds",
  DATA_LINEAGE_TABLE = "dlint",
  DATA_LINEAGE_FEATURE = "dlinf",
  DATA_LINEAGE_RESOLVER = "dlinr",
  DATA_LINEAGE_COLUMN = "dlinc",
  DATA_LINEAGE_VIEW = "dlinv",

  /** Logs Search params */
  SEARCH_TYPE = "lgst",
  LOG_SEVERITIES = "lgsv",
  SUBSCRIPTIONS = "ss",
  SIMPLE_DATE_RANGE_PRESET = "sdrp",
  LOG_ORDER = "lgo",
  LOG_ID = "lgid",
  /** Highlights + scrolls to a log without opening its modal (unlike LOG_ID). */
  LOG_HIGHLIGHT_ID = "lghl",
  /** Basic Table v3 log attributes selected as columns. */
  LOG_COLUMNS = "lgcols",
  /** The same, for the access logs table; separate so one page's columns can't apply to the other. */
  ACCESS_LOG_COLUMNS = "alcols",
  /** Inner tab (flame graph / waterfall / ...) of the log modal's Trace tab. */
  LOG_TRACE_VIEW = "lgtv",
  /** Structured aggregation-builder state, encoded compactly (see aggregationBuilderUrl). */
  LOG_AGGREGATION = "lgagg",

  /** Offline Tiles: timeline group-by dimensions, outermost first. */
  TILES_GROUP_BYS = "tgb",
  /** Offline Tiles: selected timeline row (materialization key hash) or group key. */
  TILES_SELECTED_KEY = "tsel",
  /** Offline Tiles: whether coverage-gap markers are drawn on the timeline. */
  TILES_SHOW_GAPS = "tgaps",

  /** Usage credits: group-by facet paths, outermost first. */
  USAGE_GROUP_BYS = "ugb",
  /** Usage credits: the daily chart's lookback in days. */
  USAGE_DAYS_BACK = "udays",
  /** Usage credits: the monthly summary's selected month, counted back from the newest. */
  USAGE_MONTH_INDEX = "umi",

  /** Kube Events: synthetic event identifier for deep-linking to a specific event */
  KUBE_EVENT_ID = "keid",

  /** Notebook cell deep link: selects + scrolls to the cell on load, follows selection */
  NOTEBOOK_CELL_ID = "nbcell",

  /** Notebook side drawer: which pane is open ("settings" | "history" | "dependencies"; absent = closed) */
  NOTEBOOK_PANEL = "nbpanel",

  /** Notebook view-only mode ("read"; absent = editable) */
  NOTEBOOK_VIEW = "nbview",

  /** Dashboard view-only mode ("read"; absent = editable) */
  DASHBOARD_VIEW = "dashview",

  /** Error message search */
  MESSAGE = "msg",

  /** Operator Table */
  OPERATOR_TYPE = "ot",
  PIPELINE_IDS = "plids",
  SHARD_IDS = "shids",
  BATCH_OP_TYPE = "bopt",
  VELOX_OP_TYPE = "vopt",
  STATIC_OP_TYPE = "sopt",

  /** Perf Summary top-level single shard filter */
  PERF_SUMMARY_SHARD_ID = "psid",
  /** Perf Summary viewer tab selection */
  PERF_SUMMARY_VIEW = "pf",

  /** Chart params */
  CHART_ALERT_SEVERITY = "chas",
  // use this even if group by is expected one or none
  CHART_GROUP_BYS = "chgbs",
  CHART_LINKED_ENTITY_KIND = "chek",
  CHART_LINKED_ENTITY_ID = "cheid",
  CHART_PLOT_STYLE = "chps",
  CHART_STYLE = "chs",
  /**
   * Dashboard control-bar annotation toggles (URL-persisted). Each chart grid that opts into URL
   * persistence shares these page-global params; absence means the default (off / unset scope).
   */
  DASHBOARD_SHOW_INCIDENTS = "dndi",
  DASHBOARD_SHOW_INCIDENT_RANGES = "dndir",
  DASHBOARD_SHOW_DEPLOYMENTS = "dndd",
  DASHBOARD_SHOW_KUBE_PATCHES = "dndk",
  DASHBOARD_SHOW_CUSTOM_ANNOTATIONS = "dndca",
  DASHBOARD_INCIDENT_SCOPE = "dndis",

  /** Metadata jobs (PG Cron) params */
  METADATA_JOB_STATUS_PROTO = "mjs_p",
  METADATA_SCHEDULED_JOB_TAG = "msjt",
  /** Data plane job queue params */
  // Jobs-table filter chips. String-typed because a server chip serializes as
  // "<operator>::<value>" (e.g. "is any of::Running"), which an integer parser
  // cannot round-trip; the label→enum mapping happens in the chip's encoder.
  JOB_QUEUE_STATE_FILTER = "jqsf",
  JOB_QUEUE_KIND_FILTER = "jqkf",
  JOB_OPERATION_ID_FILTER = "joid",

  /** Offline wide-table runs table: manual-vs-scheduled trigger filter chip. */
  OFFLINE_WIDE_TABLE_TRIGGER_KIND_FILTER = "owttk",

  /** Offline wide-table runs table: fill-vs-compaction kind filter chip. */
  OFFLINE_WIDE_TABLE_KIND_FILTER = "owtk",

  /** Rerun params: passed when navigating to the data explorer from a query run */
  RERUN_OPERATION_ID = "roi",
  RERUN_TIMESTAMP = "rts",

  /** Compute list page filters */
  COMPUTE_STATUS = "cst",
  COMPUTE_TAGS = "ctg",
  COMPUTE_MODEL_NAME = "cmn",
  DOCKER_IMAGES = "dis",
  /** Mount path selected in a resource's attached-volumes tab */
  VOLUME_MOUNT = "vmount",
  VOLUME_PREFIX = "vpx",
  /** Full path of the file opened in the volume file viewer */
  VOLUME_FILE = "vfile",
  /** Version id being browsed in the volume file viewer; absent means latest ("main") */
  VOLUME_VERSION = "vver",
  /** Ref (fork) being browsed in the volume file viewer. Absent means "main" */
  VOLUME_REF = "vref",

  /** Materialized aggregate tiles page */
  TILES_QUERY = "tcq",
}

/** Offline Tiles' Timeline/Features switch, as `tiles.v`; namespaced because test panes own `v`. */
export const TILES_VIEW_QUERY_NAMESPACE = "tiles";
/** Offline Tiles' drawer tab for the selected row, as `tiles-drawer.v`; absent for Info. */
export const TILES_DRAWER_QUERY_NAMESPACE = "tiles-drawer";

/**
 * Query Params not meant to be used to store stateful information
 * ex. attached while navigating from page to page
 *
 * TODO(Kelvin) assert that this and DynamicQueryParam are disjoint
 * */
export enum StaticQueryParam {
  /** The "source" of how the user got to this page */
  NAVIGATION_SOURCE = "ns",
}
